/**
 * Import d'un export **Avast Passwords** JSON (« Parametres > Importer /
 * exporter > Exporter » d'Avast Passwords, arrete par Avast : ses
 * utilisateurs doivent pouvoir partir avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier porte trois listes : `logins` (identifiants),
 * `notes` (notes securisees) et `cards` (cartes bancaires, expiration en
 * `{ month, year }`). Toute autre cle d'un element devient un champ
 * personnalise, masque si son libelle designe un secret (aucune perte).
 * L'export CSV d'Avast (`name,web,login,password`) passe par le CSV generique.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, type ImportedEntry } from './csv-import.js';
import { normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asArray(value: Json): readonly Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Cles lues par la fiche, par liste : elles ne redeviennent pas des champs. */
const LOGIN_KEYS = new Set(['custName', 'url', 'loginName', 'pwd', 'note']);
const NOTE_KEYS = new Set(['label', 'text']);
const CARD_KEYS = new Set(['custName', 'note', 'holderName', 'cardNumber', 'cvv', 'expirationDate']);

/** Metadonnees d'un element, sans valeur pour l'utilisateur. */
const IGNORED_KEYS = /^(?:id|guid|uuid|created|modified|lastused|lastmodified|color|colour|icon)$/iu;

/** Cles non reprises par la fiche -> champs personnalises (masques si secrets). */
function extraFields(item: Record<string, Json>, known: ReadonlySet<string>): EntryCustomField[] {
  const fields: EntryCustomField[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (known.has(key) || IGNORED_KEYS.test(key)) continue;
    const text = asString(value);
    if (text.length > 0) fields.push(csvColumnField(key, text));
  }
  return fields;
}

function withFields(customFields: readonly EntryCustomField[]): { customFields?: readonly EntryCustomField[] } {
  return customFields.length > 0 ? { customFields } : {};
}

function parseAvastLogin(item: Record<string, Json>): ImportedEntry {
  const url = asString(item['url']);
  const username = asString(item['loginName']);
  return {
    type: 'login',
    name: asString(item['custName']) || url || username || 'Sans nom',
    username,
    // Le mot de passe n'est jamais rogne : une espace peut en faire partie.
    password: typeof item['pwd'] === 'string' ? item['pwd'] : asString(item['pwd']),
    uris: url.length > 0 ? [url] : [],
    notes: asString(item['note']),
    totpSecret: '',
    favorite: false,
    ...withFields(extraFields(item, LOGIN_KEYS)),
  };
}

function parseAvastNote(item: Record<string, Json>): ImportedEntry {
  return {
    type: 'note',
    name: asString(item['label']) || 'Sans nom',
    username: '',
    password: '',
    uris: [],
    // Le texte d'une note garde sa mise en forme (lignes, retraits).
    notes: typeof item['text'] === 'string' ? item['text'] : asString(item['text']),
    totpSecret: '',
    favorite: false,
    ...withFields(extraFields(item, NOTE_KEYS)),
  };
}

function parseAvastCard(item: Record<string, Json>): ImportedEntry {
  const typed: Record<string, string> = {};
  const set = (key: string, value: string): void => {
    if (value.length > 0) typed[key] = value;
  };
  set('cardholder', asString(item['holderName']));
  set('number', asString(item['cardNumber']));
  set('cvv', asString(item['cvv']));
  const customFields = extraFields(item, CARD_KEYS);
  // Expiration `{ month, year }` : complete -> MM/AAAA ; a moitie -> champ, jamais perdue.
  const expiration = asObject(item['expirationDate']);
  const month = asString(expiration?.['month']);
  const year = asString(expiration?.['year']);
  if (month.length > 0 && year.length > 0) set('expiry', `${month}/${year}`);
  else if (month.length > 0 || year.length > 0) customFields.push({ name: 'Expiration', value: month || year, hidden: false });
  return {
    type: 'card',
    name: asString(item['custName']) || 'Carte',
    username: '',
    password: '',
    uris: [],
    notes: joinNotes([asString(item['note'])]),
    totpSecret: '',
    favorite: false,
    fields: normalizeTypedFields('card', typed),
    ...withFields(customFields),
  };
}

function objects(value: Json): Record<string, Json>[] {
  return asArray(value)
    .map(asObject)
    .filter((item): item is Record<string, Json> => item !== null);
}

/**
 * Un export Avast se reconnait a ses listes `logins` / `notes` / `cards` dont
 * un element porte une cle propre a Avast (`custName`, `loginName`, `pwd`,
 * `cardNumber`, ou le couple `label` + `text` d'une note) : une simple liste
 * `notes` d'un autre JSON ne suffit pas.
 */
export function isAvastExport(data: Json): boolean {
  const file = asObject(data);
  if (file === null) return false;
  const login = objects(file['logins']).some((item) => 'loginName' in item || 'pwd' in item || 'custName' in item);
  const note = objects(file['notes']).some((item) => 'label' in item && 'text' in item);
  const card = objects(file['cards']).some((item) => 'cardNumber' in item || 'holderName' in item);
  return login || note || card;
}

/** Parse un export Avast Passwords JSON : identifiants, puis notes, puis cartes. */
export function parseAvastExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  if (file === null) throw new Error('export Avast : structure inattendue');
  return [
    ...objects(file['logins']).map(parseAvastLogin),
    ...objects(file['notes']).map(parseAvastNote),
    ...objects(file['cards']).map(parseAvastCard),
  ];
}
